<template>
  <div class="bill-form px-3">
    <form @submit.stop.prevent="form.handleSubmit()">
      <!-- upload attachment btn & modal -->
      <div>
        <BaseButton
          variant="secondary"
          text="Upload bill document (optional)"
          type="button"
          icon-class="i-mdi-paperclip"
          @click="open = true"
        />

        <Modal
          v-model="open"
          title="Upload bill document"
          width="w-full md:w-1/2"
          close-button-text="Close"
          :no-escape="true"
          :no-backdrop="true"
          :no-close-button="true"
          @close="setBillAttachments"
        >
          <template #body>
            <div class="mt-4">
              <AppFileUpload
                :storage-path="`bills/documents/attachments/`"
                @on-file-upload-completed="fileUploadCompleted"
              />
            </div>
          </template>
        </Modal>
      </div>

      <!-- attachments -->
      <div class="flex flex-wrap gap-4 mt-4">
        <div
          v-for="(file, index) in bill.attachments"
          :key="file"
          class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
        >
          <img
            v-if="isImage(file)"
            :src="file"
            class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
            @click="expandImage(file)"
          >
          <div
            v-else
            class="flex items-center justify-center w-full h-full text-center bg-slate-200 rounded-lg cursor-pointer"
            @click="expandImage(file)"
          >
            <div class="text-dark font-weight-bold">
              {{ fileTypeToString(file) }}
            </div>
          </div>
          <span
            class="absolute top-0 right-0 p-1 flex justify-center items-center rounded-full border-transparent bg-black/50 hover:bg-black/75 transition duration-200 ease-in-out cursor-pointer overflow-hidden select-none size-[1.3rem]"
            @click="deleteAttachment(index)"
          >
            <span class="i-mdi-close text-white"></span>
          </span>
        </div>
      </div>

      <!-- Radios: PO or WO -->
      <div class="grid grid-cols-2 mt-8 gap-8">
        <div>
          <BaseRadio
            v-model="billType"
            name="billType"
            value="purchase"
            label="Purchase Orders"
            @change="empty()"
          />
        </div>

        <div>
          <BaseRadio
            v-model="billType"
            name="billType"
            value="work"
            label="Work Orders"
            @change="empty()"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-8">
        <div>
          <form.Field
            v-if="billType === 'purchase'"
            name="purchaseOrderId"
            :validators="{
              onBlur: yup.string().required('Purchase Order is required')
            }"
          >
            <template #default="{ field }">
              <BaseSelect
                :model-value="(field.state.value as string)"
                :name="field.name"
                :options="purchaseOrders"
                label="Purchase Orders"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>

          <form.Field
            v-else
            name="workOrderId"
            :validators="{
              onBlur: yup.string().required('Work Order is required')
            }"
          >
            <template #default="{ field }">
              <BaseSelect
                :model-value="(field.state.value as string)"
                :name="field.name"
                :options="workOrders"
                label="Work Orders"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <div>
          <form.Field
            name="billNumber"
            :validators="{
              onBlur: yup.string().required('Bill Number is required')
            }"
          >
            <template #default="{ field }">
              <BaseInput
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="BILL#"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-8">
        <div>
          <form.Field
            name="billDate"
            :validators="{
              onBlur: yup.string().required('Invoice Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Invoice Date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <div>
          <form.Field
            name="dueDate"
            :validators="{
              onBlur: yup.string().required('Due Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Due date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <h4 class="mt-6">Items</h4>
      <hr class="mt-2">

      <div v-if="existingBillsLoading" class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
        <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
        <p class="text-sm text-center text-slate-500 pt-4">
          Loading...
        </p>
      </div>

      <!-- items -->
      <div v-else>
        <form.Field
          name="items"
          :validators="{
            onChange: yup.array().min(1, 'At least one item is required')
          }"
        >
          <template #default="{ field }">
            <div
              v-if="(field.state.meta.errors && field.state.meta.errors[0]) && form.getFieldValue('items').length === 0"
              class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
            >
              <span class="i-heroicons-exclaimation-circle size-8" />
              <p class="text-base/6 text-error sm:text-sm/6">
                {{ field.state.meta.errors[0] }}
              </p>
            </div>

            <div
              v-for="(_, i) of field.state.value"
              :key="`item-${i}`"
            >
              <div class="flex justify-end mt-8">
                <span
                  class="i-heroicons-x-circle cursor-pointer hover:text-error h-[1.2rem] w-[1.2rem]"
                  @click="removeItem(field, Number(i))"
                />
              </div>

              <!-- Title, Quantity, Price -->
              <div class="flex justify-around gap-6">
                <div class="grow">
                  <form.Field
                    :key="`name-${i}`"
                    :name="( `items[${i}].name` as any)"
                    :validators="{
                      onBlur: yup.string().required('Title is required'),
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        :model-value="(subField.state.value as string)"
                        type="text"
                        label="Title"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => subField.handleChange(e.target.value)"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2">
                  <form.Field
                    :key="`quantity-${i}`"
                    :name="(`items[${i}].quantity` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Quantity must be a positive number',(value) => !isNaN(Number(value)) && Number(value) > 0).required('Quantity is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        v-model.number="(subField.state.value as string)"
                        type="number"
                        step="any"
                        :label="billType === 'purchase' ? 'Quantity' : 'Hours'"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="grow">
                  <form.Field
                    :key="`unitPrice-${i}`"
                    :name="(`items[${i}].unitPrice` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Price must be a valid number',(value) => !isNaN(Number(value))).required('Price is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInputCurrency
                        :model-value="(subField.state.value as string)"
                        label="Price"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2 min-w-24">
                  <label
                    class="font-semibold"
                    for="total-price"
                  >Total</label>
                  <span
                    id="total-price"
                    class="block mt-3"
                  >{{ currency(toNumber((field.state.value[i] as any).quantity) * toNumber((field.state.value[i] as any).unitPrice)) }}</span>
                </div>
              </div>
            </div>

            <div class="my-5">
              <BaseButton
                variant="secondary"
                type="button"
                text="Add item"
                :disabled="!isPOorWOselected"
                :class="!isPOorWOselected ? 'opacity-50' : ''"
                @click="addItem(field)"
              />
            </div>
          </template>
        </form.Field>
      </div>

      <div
        v-if="isPOorWOselected"
        class="mt-5 w-fit ml-auto"
      >
        <div class="flex items-baseline justify-end bg-slate-100 px-4 pt-4 rounded-lg gap-4">
          <span class="text-slate-700">Total</span>
          <form.Field
            name="total"
            :validators="{
              onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Total must be a valid number',(value) => !isNaN(Number(value))).required('Total is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputCurrency
                :model-value="(field.state.value as string)"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ListBills' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>
</template>
<script lang="ts" setup>
import { Bill, BillItem, generateBillId } from '@/models/bill'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseRadio from '@/components/base/BaseRadio.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import Modal from '@/components/app/AppModal.vue'
import BillService from '@/services/BillService'
import { currency, toNumber } from '@/utils'
import { computed, PropType, ref, watch } from 'vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { decimalFormat } from '@/utils'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { extend, map } from 'underscore'
import StorageService from '@/services/StorageService'
import { useNotification } from '@kyvg/vue3-notification'

type selectOption = {
  label: string
  value: string,
  disabled?: boolean
  [key: string]: any
}

const props = defineProps({
  workOrders: {
    type: Array as PropType<selectOption[]>,
    default: () => []
  },
  purchaseOrders: {
    type: Array as PropType<selectOption[]>,
    default: () => []
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const bill = ref(new Bill())
const billType = ref('purchase')
const billService = new BillService()
const open = ref(false)
const tempAttachments = ref([])
const storageService = new StorageService()
const { notify } = useNotification()
const queryClient = useQueryClient()

const setBillAttachments = () => {
  open.value = false
  bill.value.attachments = tempAttachments.value
}

const fileUploadCompleted = (urls: string[]) => {
  tempAttachments.value = urls
}

const form = useForm({
  defaultValues: bill.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const newBill = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as Bill

    if (newBill.purchaseOrderId) {
      const purchaseOrder = props.purchaseOrders.find((po) => po.value === newBill.purchaseOrderId)
      newBill.purchaseOrderId = purchaseOrder?.purchaseOrderId
      newBill.projectId = purchaseOrder?.projectId
    } else if (newBill.workOrderId) {
      const workOrder = props.workOrders.find((wo) => wo.value === newBill.workOrderId)
      newBill.workOrderId = workOrder?.workOrderId
      newBill.projectId = workOrder?.projectId
    }

    newBill.items = newBill.items.map((item) => {
      const billItem = {
        ...item,
        quantity: toNumber(item.quantity),
        unitPrice: toNumber(item.unitPrice),
        total: item.quantity * toNumber(item.unitPrice)
      }
      return Object.fromEntries(Object.entries(billItem).filter(([, v]) => v !== undefined)) as unknown as BillItem
    })
    newBill.total = toNumber(newBill.total)
    newBill.balance = newBill.total
    newBill.attachments = tempAttachments.value
    newBill.dueDate = new Date(newBill.dueDate)
    newBill.billDate = new Date(newBill.billDate)
    await props.saveFunction(newBill)
    queryClient.invalidateQueries({ queryKey: ['existingBills'] })
  }
})

const selectedPOId = form.useStore((state) => state.values.purchaseOrderId)
const selectedWOId = form.useStore((state) => state.values.workOrderId)
const isPOorWOselected = computed(() => {
  return selectedPOId.value || selectedWOId.value ? true : false
})

const { data:otherBills, isLoading:existingBillsLoading } = useQuery({
  queryKey: ['existingBills', selectedPOId, selectedWOId],
  queryFn: async () => {
    let bills = []

    if (billType.value === 'purchase') {
      const purchaseOrder = props.purchaseOrders.find((po) => po.value === selectedPOId.value)
      bills = await billService.getByPurchaseOrderId(purchaseOrder.purchaseOrderId)
    } else {
      const workOrder = props.workOrders.find((wo) => wo.value === selectedWOId.value)
      bills = await billService.getByWorkOrderId(workOrder.workOrderId)
    }
    return bills
  },

  enabled: isPOorWOselected
})

watch(() => [existingBillsLoading.value, selectedPOId.value, selectedWOId.value], async () => {
  const poOrWOselected = selectedPOId.value || selectedWOId.value
  if (!existingBillsLoading.value && otherBills.value && poOrWOselected) {
    if (billType.value === 'purchase') {
      const purchaseOrder = props.purchaseOrders.find((po) => po.value === selectedPOId.value)
      setBillItemsPO(purchaseOrder, otherBills.value)
    } else {
      const workOrder = props.workOrders.find((wo) => wo.value === selectedWOId.value)
      setBillItemsWO(workOrder, otherBills.value)
    }
    if (!form.getFieldValue('billNumber')) {
      form.setFieldValue('billNumber', await generateBillId())
      form.validateField('billNumber', 'blur')
    }
  }
})


const setBillItemsPO = async (purchaseOrder, existingBills) => {
  form.setFieldValue('items', [])
  form.setFieldValue('payee', purchaseOrder.vendorId)
  let poItems = JSON.parse(JSON.stringify(purchaseOrder.items))

  if (existingBills.length > 0) {
    // If there are existing bills, we need to check if the items already exist
    const existingItems = map(existingBills, (bill) => bill.items).flat().filter(item => item.id || item.name)

    // Filter out items that already exist in the bill
    poItems = poItems.filter(item => !existingItems.some(existingItem => item.lineItems.includes(existingItem.id) || existingItem.name === item.name))
  }

  await form.setFieldValue('items', map(poItems, (item) => {
    return extend(new BillItem(), {
      id: item.id,
      purchaseOrderId: purchaseOrder.purchaseOrderId,
      name: item.name,
      quantity: toNumber(item.quantity),
      unitPrice: toNumber(item.cost),
      total: toNumber(item.quantity) * toNumber(item.cost),
      projectId: purchaseOrder.projectId
    })
  }))
  form.validateField('items', 'change')
  setTotal()
}

const setBillItemsWO = async (workOrder, existingBills) => {
  form.setFieldValue('items', [])
  form.setFieldValue('payee', workOrder.subId)
  let workOrderItems = JSON.parse(JSON.stringify(workOrder.items))

  if (existingBills.length > 0) {
    // If there are existing bills, we need to check if the items already exist
    const existingItems = map(existingBills, (bill) => bill.items).flat().filter(item => item.id || item.name)

    // Filter out items that already exist in the bill
    workOrderItems = workOrderItems.filter(item => !existingItems.some(existingItem => item.lineItems.includes(existingItem.id) || existingItem.name === item.name))
  }

  await form.setFieldValue('items', map(workOrderItems, (workOrderItem) => {
    return extend(new BillItem(), {
      id: workOrderItem.id,
      workOrderId: workOrder.workOrderId,
      name: workOrderItem.name,
      quantity: 1,
      unitPrice: toNumber(workOrderItem.cost),
      total: toNumber(workOrderItem.cost),
      projectId: workOrder.projectId
    })
  }))
  form.validateField('items', 'change')
  setTotal()
}

const empty = () => {
  // to avoid validation errors
  const itemsCount = form.getFieldValue('items').length
  for (let i = 0; i < itemsCount; i++) {
    form.removeFieldValue('items', i)
  }
  form.setFieldValue('items', [])

  if (billType.value === 'purchase') {
    form.deleteField('workOrderId')
  } else {
    form.deleteField('purchaseOrderId')
  }
}

const addItem = (field: any) => {
  field.pushValue(new BillItem())
}

const removeItem = (field: any, index: number) => {
  field.removeValue(index)
  setTotal()
}

const setTotal = () => {
  const total = form.getFieldValue('items').reduce((acc, item) => {
    return acc + (item.quantity * toNumber(item.unitPrice))
  }, 0)

  if (total) {
    form.setFieldValue('total', decimalFormat(total, 2) as any)
  } else {
    form.setFieldValue('total', String(0) as any)
  }
  form.validateField('total', 'blur')
}

const isImage = (file: string) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext[1].toLowerCase()) : false
}

const deleteAttachment = async (index: number) => {
  const attachment = bill.value.attachments[index]
  try {
    await storageService.deleteAttachmentWithStyledImages(attachment)
    bill.value.attachments.splice(index, 1)
    tempAttachments.value = tempAttachments.value.filter(att => att !== attachment)
  } catch (error) {
    console.log(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Failed to delete attachment'
    })
  }
}

const fileTypeToString = (file: string) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ext[1].toUpperCase() : 'FILE'
}

const expandImage = (imageURL: string) => {
  window.open(imageURL, '_blank')
}

</script>
